import type { LatLng, RouteWaypoint } from '../types';

export const EARTH_RADIUS_M = 6_371_000;
export const SAMPLE_INTERVAL_M = 10 * 1609.34; // ~10 miles in metres (16,093.4 m)
export const MIN_SAMPLE_GAP_M = 250; // skip sample if within 250m of previous kept point

/** Haversine distance in metres between two coordinates. */
export function haversineDistanceM(a: LatLng, b: LatLng): number {
  const dLat = ((b.latitude - a.latitude) * Math.PI) / 180;
  const dLon = ((b.longitude - a.longitude) * Math.PI) / 180;
  const lat1 = (a.latitude * Math.PI) / 180;
  const lat2 = (b.latitude * Math.PI) / 180;

  const sinDLat = Math.sin(dLat / 2);
  const sinDLon = Math.sin(dLon / 2);
  const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLon * sinDLon;
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(Math.max(0, 1 - h)));
  return EARTH_RADIUS_M * c;
}

/** Calculate cumulative distances along coordinates [[lon, lat], ...]. */
export function cumulativeDistancesM(coords: [number, number][]): number[] {
  const cum: number[] = [0];
  let total = 0;
  for (let i = 1; i < coords.length; i++) {
    const prev: LatLng = { latitude: coords[i - 1]![1], longitude: coords[i - 1]![0] };
    const curr: LatLng = { latitude: coords[i]![1], longitude: coords[i]![0] };
    total += haversineDistanceM(prev, curr);
    cum.push(total);
  }
  return cum;
}

/** Interpolate [lon, lat] along a polyline at a target distance. */
export function pointAtDistance(
  coords: [number, number][],
  cumDist: number[],
  targetM: number,
): LatLng {
  if (coords.length === 0) return { latitude: 0, longitude: 0 };
  if (coords.length === 1 || targetM <= 0) {
    return { latitude: coords[0]![1], longitude: coords[0]![0] };
  }
  const totalM = cumDist[cumDist.length - 1] || 0;
  if (targetM >= totalM) {
    const last = coords[coords.length - 1]!;
    return { latitude: last[1], longitude: last[0] };
  }

  // Find segment containing targetM
  let idx = cumDist.findIndex((d) => d >= targetM);
  if (idx <= 0) idx = 1;
  const prevDist = cumDist[idx - 1]!;
  const currDist = cumDist[idx]!;
  const segLen = currDist - prevDist;
  const t = segLen > 0 ? (targetM - prevDist) / segLen : 0;

  const p0 = coords[idx - 1]!;
  const p1 = coords[idx]!;

  return {
    latitude: p0[1] + t * (p1[1] - p0[1]),
    longitude: p0[0] + t * (p1[0] - p0[0]),
  };
}

/**
 * Sample route coordinates roughly every 10 miles along the entire path.
 * Matches iOS §2.1 spec logic.
 */
export function sampleRoute(
  coords: [number, number][],
  routeDurationSec: number,
  routeDistanceM: number,
): RouteWaypoint[] {
  if (coords.length < 2) return [];

  const cum = cumulativeDistancesM(coords);
  const totalLengthM = cum[cum.length - 1] || 0;
  if (totalLengthM <= 1) return [];

  const effectiveDistance = Math.max(routeDistanceM, totalLengthM);
  const timeScale = routeDurationSec / (effectiveDistance || 1);

  const waypoints: RouteWaypoint[] = [];

  // 1. Point 0 (start)
  const startCoord: LatLng = { latitude: coords[0]![1], longitude: coords[0]![0] };
  waypoints.push({
    id: `${startCoord.latitude.toFixed(5)},${startCoord.longitude.toFixed(5)}-0`,
    coordinate: startCoord,
    distanceM: 0,
    travelTimeSec: 0,
    routeProgress: 0,
  });

  // 2. Intermediate points: adapt interval so short routes/segments have at least ~12 waypoints,
  // preventing under-marked short segments while capping max interval at SAMPLE_INTERVAL_M.
  const stepM = Math.min(SAMPLE_INTERVAL_M, Math.max(400, totalLengthM / 14));
  let currentTargetM = stepM;
  while (currentTargetM < totalLengthM - MIN_SAMPLE_GAP_M) {
    const coord = pointAtDistance(coords, cum, currentTargetM);
    const prev = waypoints[waypoints.length - 1]!;
    const gap = haversineDistanceM(prev.coordinate, coord);

    if (gap >= MIN_SAMPLE_GAP_M) {
      const travelTime = currentTargetM * timeScale;
      waypoints.push({
        id: `${coord.latitude.toFixed(5)},${coord.longitude.toFixed(5)}-${Math.round(currentTargetM)}`,
        coordinate: coord,
        distanceM: currentTargetM,
        travelTimeSec: travelTime,
        routeProgress: currentTargetM / totalLengthM,
      });
    }

    currentTargetM += stepM;
  }

  // 3. Last point (destination)
  const last = coords[coords.length - 1]!;
  const endCoord: LatLng = { latitude: last[1], longitude: last[0] };
  const prev = waypoints[waypoints.length - 1]!;
  const gapToEnd = haversineDistanceM(prev.coordinate, endCoord);

  // If previous point is too close to end, replace or append
  if (gapToEnd < MIN_SAMPLE_GAP_M && waypoints.length > 1) {
    waypoints.pop();
  }

  waypoints.push({
    id: `${endCoord.latitude.toFixed(5)},${endCoord.longitude.toFixed(5)}-${Math.round(totalLengthM)}`,
    coordinate: endCoord,
    distanceM: totalLengthM,
    travelTimeSec: routeDurationSec,
    routeProgress: 1,
  });

  return waypoints;
}

/**
 * Offset a coordinate perpendicular-left of the nearest route segment,
 * pointing generally northward so weather pins stay above the route line.
 */
export function perpOffset(
  coord: LatLng,
  routeCoords: [number, number][],
  offsetKm: number,
): LatLng {
  if (routeCoords.length < 2) return coord;

  // Find nearest segment midpoint
  let nearestIdx = 0;
  let minD = Infinity;
  for (let i = 0; i < routeCoords.length - 1; i++) {
    const midLat = (routeCoords[i]![1] + routeCoords[i + 1]![1]) / 2;
    const midLon = (routeCoords[i]![0] + routeCoords[i + 1]![0]) / 2;
    const d = Math.hypot(coord.latitude - midLat, coord.longitude - midLon);
    if (d < minD) {
      minD = d;
      nearestIdx = i;
    }
  }

  const a = routeCoords[nearestIdx]!;
  const b = routeCoords[Math.min(nearestIdx + 1, routeCoords.length - 1)]!;

  const DEG = 111_320; // metres per degree latitude
  const cosLat = Math.cos((coord.latitude * Math.PI) / 180);
  const dy = (b[1] - a[1]) * DEG; // northward m
  const dx = (b[0] - a[0]) * DEG * cosLat; // eastward m
  const len = Math.hypot(dx, dy) || 1;

  // Perpendicular unit vector (rotate 90° CCW)
  let px = -dy / len;
  let py = dx / len;

  // Flip if py < 0 so it points northward
  if (py < 0) {
    px = -px;
    py = -py;
  }

  const offsetM = offsetKm * 1000;
  return {
    latitude: coord.latitude + (py * offsetM) / DEG,
    longitude: coord.longitude + (px * offsetM) / (DEG * (cosLat || 1)),
  };
}

/** Project a point onto a polyline to find its cumulative distance along the route. */
export function alongRouteDistanceM(
  coords: [number, number][],
  point: LatLng,
): number {
  if (coords.length < 2) return 0;
  const cum = cumulativeDistancesM(coords);

  let bestDist = 0;
  let minPerpDist = Infinity;

  for (let i = 0; i < coords.length - 1; i++) {
    const p0 = coords[i]!;
    const p1 = coords[i + 1]!;
    const segLen = cum[i + 1]! - cum[i]!;

    // Project using flat equirectangular approximation for nearest point on segment
    const cosLat = Math.cos((point.latitude * Math.PI) / 180);
    const dx = (p1[0] - p0[0]) * cosLat;
    const dy = p1[1] - p0[1];
    const segLenDeg2 = dx * dx + dy * dy;

    let t = 0;
    if (segLenDeg2 > 0) {
      const px = (point.longitude - p0[0]) * cosLat;
      const py = point.latitude - p0[1];
      t = Math.max(0, Math.min(1, (px * dx + py * dy) / segLenDeg2));
    }

    const projLat = p0[1] + t * (p1[1] - p0[1]);
    const projLon = p0[0] + t * (p1[0] - p0[0]);
    const distToPoint = haversineDistanceM(point, { latitude: projLat, longitude: projLon });

    if (distToPoint < minPerpDist) {
      minPerpDist = distToPoint;
      bestDist = cum[i]! + t * segLen;
    }
  }

  return bestDist;
}

/** Point along polyline by progress fraction [0, 1]. */
export function pointAlongProgress(
  coords: [number, number][],
  progress: number,
): LatLng {
  if (coords.length === 0) return { latitude: 0, longitude: 0 };
  const cum = cumulativeDistancesM(coords);
  const total = cum[cum.length - 1] || 0;
  const target = Math.max(0, Math.min(1, progress)) * total;
  return pointAtDistance(coords, cum, target);
}

export type BoundsBox = {
  west: number;
  south: number;
  east: number;
  north: number;
};

/** Checks if a coordinate is within a bounding box, supporting antimeridian wrap. */
export function isInBounds(
  coord: LatLng,
  b: BoundsBox,
): boolean {
  const inLat = coord.latitude >= b.south && coord.latitude <= b.north;
  const inLng =
    b.west <= b.east
      ? coord.longitude >= b.west && coord.longitude <= b.east
      : coord.longitude >= b.west || coord.longitude <= b.east;
  return inLat && inLng;
}
